import React from 'react';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { PersistGate } from 'redux-persist/integration/react';
import { persistStore } from 'redux-persist';
import { StyledEngineProvider } from '@mui/material/styles';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.min.css';

import App from './App';
import { getConfig } from './AppConfig';
import store from './redux/legacy/stores/GlobalStore';
import ThemeProvider from './components/layout/Theme/ThemeProvider';
import SeoProvider from './components/providers/SeoProvider/SeoProvider';

const persistor = persistStore(store);

const config = getConfig();

if (config?.bugsnagKey && /^[a-f0-9]{32}$/i.test(config.bugsnagKey)) {
  Bugsnag.start({
    apiKey: config.bugsnagKey,
    plugins: [new BugsnagPluginReact()],
    appVersion: process.env.REACT_APP_VERSION || '0.0.0',
    enabledReleaseStages: ['production'],
    onError(event) {
      // ChunkLoadError — and its SyntaxError variant ("Unexpected token '<'") —
      // fire when a deploy has replaced old chunk files and the browser fetches
      // a stale hash-filename that no longer exists; the server returns the HTML
      // index page, which JS then fails to parse. lazyWithRetry auto-reloads so
      // these self-heal. Suppress both forms so they don't pollute the dashboard.
      if (event.errors?.[0]?.errorClass === 'ChunkLoadError') return false;
      if (
        event.errors?.[0]?.errorClass === 'SyntaxError' &&
        /Unexpected token '<'/.test(event.errors?.[0]?.errorMessage ?? '')
      )
        return false;

      const frames = event.errors?.[0]?.stacktrace ?? [];
      const isThirdPartyWebKit =
        frames.length > 0 &&
        frames.every(
          (f) => !f.file || f.file.startsWith('webkit-masked-url://')
        );
      if (isThirdPartyWebKit) return false;

      // Stripe client-side validation messages (e.g. "Your card number is
      // incomplete.", "Your card's expiration date is incomplete.") are
      // user-input edge cases, not application errors. The call site in
      // EmbeddedCheckout already guards against these; this is a safety net.
      const errorMessage = event.errors?.[0]?.errorMessage ?? '';
      if (/^Your card/i.test(errorMessage)) return false;

      return true;
    },
  });
}
const ErrorBoundary = Bugsnag.getPlugin('react')
  ? Bugsnag.getPlugin('react').createErrorBoundary(React)
  : ({ children }) => children;

function Root() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <BrowserRouter>
            <StyledEngineProvider injectFirst>
              <ThemeProvider>
                <App />
              </ThemeProvider>
            </StyledEngineProvider>
            <SeoProvider />
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  );
}
export default Root;
